import React, { useContext, useState, useEffect } from "react";
import "../FlightRoster.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  AirportType,
  DisputeStatus,
  FLIGHT_PLAN_STATUS,
  REJECTION_REASONS_COMMERCIAL,
} from "../../../../utils/constants";
import specialOnboardingIcon from "../../../../assets/images/special-onboarding-icon.svg";
import infoIcon from "../../../../assets/images/info_icon.svg";
import VerificationIcon from "../../../../components/element/VerificationIcon";
import { Tooltip } from "@mui/material";
import { StorageContext } from "../../../../services/StorageProvider";
import {
  getAirportCode,
  getAirportData,
  getFlightNumber,
} from "../../../../utils/utils";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { useFormData } from "../../../../helpers/FormDataProvider";

dayjs.extend(utc);

const FlightRosterCardComponent = ({
  isAAUser,
  item,
  onClickCard,
  airlineNames,
  isDisputeTab = false,
  rowHasVerification = false,
}) => {
  const { fetchAirportById } = useAirlineAdminApi();
  const { airportData, setAirportData } = useContext(StorageContext);
  const [departureAirportName, setDepartureAirportName] = useState("");
  const [arrivalAirportName, setArrivalAirportName] = useState("");
  const { userData } = useFormData();

  const getAirportName = async (airportId) => {
    // Validate airportId exists before proceeding
    if (!airportId) {
      return "";
    }

    const airport = getAirportData(airportData, airportId);
    if (airport === null) {
      // Fetch from API if not in local cache
      const response = await fetchAirportById(airportId);

      // Cache the fetched airport in local storage for future use
      if (response && setAirportData) {
        setAirportData((prevAirportData) => [
          ...(prevAirportData || []),
          response,
        ]);
      }

      return getAirportCode(response);
    }
    return getAirportCode(airport);
  };

  useEffect(() => {
    const fetchAirportNames = async () => {
      // Only call API if local data is not available
      const depName = await getAirportName(item.departureAirportId);
      const arrName = await getAirportName(item.arrivalAirportId);
      setDepartureAirportName(depName);
      setArrivalAirportName(arrName);
    };

    fetchAirportNames();
  }, [item.departureAirportId, item.arrivalAirportId, airportData]);

  // Helper function to get airport data by ID
  const getAirportById = (airportId) => {
    return airportData?.find((airport) => airport.id === airportId);
  };

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AirportType.DOMESTIC;
  };

  // Helper function to get terminal name by ID
  const getTerminalName = (airportId, terminalId) => {
    const airport = getAirportById(airportId);
    if (!airport || !airport.terminals) return "";
    const terminal = airport.terminals.find((t) => t.id === terminalId);
    return terminal?.name || "";
  };

  // Helper function to get default terminal for DOM airports
  const getDefaultTerminal = () => {
    // When no terminal is selected, show "Main Terminal" as default
    return "Main Terminal";
  };

  // Helper function to get rejection reason message
  const getRejectionReasonMessage = (reason) => {
    return REJECTION_REASONS_COMMERCIAL[reason] || reason;
  };

  // Helper function to get departure terminal
  const getDepartureTerminal = () => {
    if (!isDOMAirport(item.departureAirportId)) return null;

    const airport = getAirportById(item.departureAirportId);
    if (!airport) return null;

    // If there's a specific terminal ID, use it (check for undefined, null, and empty string)
    if (
      item.departureAirportTerminalId &&
      item.departureAirportTerminalId !== ""
    ) {
      const terminalName = getTerminalName(
        item.departureAirportId,
        item.departureAirportTerminalId
      );
      if (terminalName) return terminalName;
    }

    // Otherwise, show "Main Terminal" as default
    return getDefaultTerminal();
  };

  // Helper function to get arrival terminal
  const getArrivalTerminal = () => {
    if (!isDOMAirport(item.arrivalAirportId)) return null;

    const airport = getAirportById(item.arrivalAirportId);
    if (!airport) return null;

    // If there's a specific terminal ID, use it (check for undefined, null, and empty string)
    if (item.arrivalAirportTerminalId && item.arrivalAirportTerminalId !== "") {
      const terminalName = getTerminalName(
        item.arrivalAirportId,
        item.arrivalAirportTerminalId
      );
      if (terminalName) return terminalName;
    }

    // Otherwise, show "Main Terminal" as default
    return getDefaultTerminal();
  };

  const adsbVerified = Boolean(item?.adsbVerified);
  const bmsVerified = Boolean(item?.bmsVerified);

  const showVerificationIcon = userData?.isAAUser && (adsbVerified || bmsVerified);
  const shouldApplyTallLayout = rowHasVerification || showVerificationIcon;

  return (
    <div
      className={`flight-roster-card${shouldApplyTallLayout ? " flight-roster-card--has-verification" : ""
        }`}
      onClick={onClickCard}
    >
      <div className="flight-roster-card-header">
        <span className="flight-code">{getFlightNumber(item)}</span>
        <div>
          {isDisputeTab ? (
            <span className="dispute">Disputed</span>
          ) : (
            <>
              {item.status === FLIGHT_PLAN_STATUS.CANCELLED ? (
                <span className="cancelled">Cancelled</span>
              ) : item.disputeStatus === DisputeStatus.RAISED ? (
                <span className="dispute">Disputed</span>
              ) : item.disputeStatus === DisputeStatus.AA_FORCED_BMS_COUNT ? (
                <span className="approved">AA Count Forced</span>
              ) : (
                <>
                  {!isAAUser &&
                    item.status === FLIGHT_PLAN_STATUS.AIRLINE_APPROVAL ? (
                    <span className="approved">Approved</span>
                  ) : null}

                  {item.status === FLIGHT_PLAN_STATUS.AA_APPROVAL ? (
                    <span className="approved">Approved</span>
                  ) : null}

                  {item.requiresEscortService ? (
                    <img
                      style={{ marginLeft: "10px" }}
                      src={specialOnboardingIcon}
                      alt="special-onboarding-icon"
                    />
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="flight-roster-card-airline-name">
        <span>{airlineNames[item?.airlineId] || ""}</span>
        {(() => {
          const departureTerminal = getDepartureTerminal();
          const arrivalTerminal = getArrivalTerminal();

          // Show terminal for DOM airports
          if (isDOMAirport(item.departureAirportId) && departureTerminal) {
            return (
              <span className="departure-terminal">{departureTerminal}</span>
            );
          }

          if (isDOMAirport(item.arrivalAirportId) && arrivalTerminal) {
            return (
              <span className="departure-terminal">
                {arrivalTerminal}
                {item?.fboFlightRejectionReason && (
                  <Tooltip
                    title={getRejectionReasonMessage(
                      item?.fboFlightRejectionReason
                    )}
                    arrow
                  >
                    <img
                      src={infoIcon}
                      alt="info"
                      className="flight-roster-card-rejection-info-icon"
                    />
                  </Tooltip>
                )}
              </span>
            );
          }

          return null;
        })()}
      </div>
      <div className="flight-roster-card-divider"></div>
      <div className="flight-roster-card-details">
        <div className="flight-roster-card-detail-item">
          <span className="flight-roster-card-details-label">Departure</span>
          <span className="flight-roster-card-details-value ">
            {`${departureAirportName} - ${dayjs(item.departureTime).format(
              "DD MMM - HH:mm"
            )}`}{" "}
          </span>
        </div>
        <div className="flight-roster-card-detail-item">
          <span className="flight-roster-card-details-label">Arrival</span>
          <span className="flight-roster-card-details-value ">
            {`${arrivalAirportName} - ${dayjs(item.arrivalTime).format(
              "DD MMM - HH:mm"
            )}`}{" "}
          </span>
        </div>
        <div className="flight-roster-card-detail-item">
          <span className="flight-roster-card-details-label">
            Passenger Count
          </span>
          <span className="flight-roster-card-details-value ">
            {isDisputeTab
              ? item.airlinePassengerCount
              : item.acceptedPassengerCount}
          </span>
        </div>
      </div>
      {showVerificationIcon && (
        <VerificationIcon
          adsbVerified={adsbVerified}
          bmsVerified={bmsVerified}
          className="flight-roster-verification-icon"
        />
      )}
    </div>
  );
};

export default FlightRosterCardComponent;
